import { useMemo, useRef } from "react";
import Select from "react-select";
import { XIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import ErrorSelect from "../../../ErrorSelect";
import { MAX_ITEM_TAXES } from "../../../../Constants";

// Tailwind gray-300 / gray-500 / gray-900 / blue-50 / blue-500 / blue-900, so
// the select reads as the FormSelect beside it rather than react-select's own
// theme (#ccc border, 4px radius, #808080 placeholder, no focus change).
const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  // 36px like the inputs and selects beside it (react-select's own floor is
  // 38px). Focus is a 2px blue ring: a 1px border plus a 1px shadow.
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    borderRadius: 6,
    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
    boxShadow: state.isFocused
      ? "0 0 0 1px #3b82f6, 0 1px 2px 0 rgb(0 0 0 / 0.05)"
      : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#d1d5db" },
  }),
  placeholder: (base) => ({ ...base, color: "#6b7280" }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 7,
    color: "#6b7280",
    "&:hover": { color: "#374151" },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, borderRadius: 6 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#eff6ff" : "transparent",
    color: state.isFocused ? "#1e3a8a" : "#111827",
  }),
};

// An invoice line can carry several taxes at once — a PPN and a PPh 23 on the
// same service, say. A multi-select would have to fit in a table cell next to
// four other fields, so the taxes already on the line are chips and the select
// below only ever adds one: it holds no value of its own, offers only taxes
// that are not on the line yet, and resets itself after each pick. Duplicates
// are impossible by construction rather than by validation, which is what the
// backend rejects the save for.
const ItemTaxPicker = ({
  id,
  options = [],
  value = [],
  onChange,
  max = MAX_ITEM_TAXES,
  isLoading = false,
  isDisabled = false,
  error = null,
  onRefresh,
  placeholder,
}) => {
  const { t } = useTranslation();
  const selectRef = useRef(null);

  // A screen that has not adopted the picker yet passes nothing, which reads
  // as a line with no taxes rather than crashing the row.
  const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedIDs = useMemo(
    () => new Set(selected.map((tax) => tax?.id ?? tax?.value).filter(Boolean)),
    [selected],
  );

  // A tax the business has since deleted is still on the line and still has to
  // be saved with it, so the chip is rendered from whatever the line knows.
  // Its name is looked up here rather than stored on the line, so a chip built
  // before the list arrived is named as soon as it does.
  const byID = useMemo(
    () => new Map((options || []).map((tax) => [tax.id, tax])),
    [options],
  );

  const available = useMemo(
    () =>
      (options || [])
        .filter((tax) => !selectedIDs.has(tax.id))
        .map((tax) => ({ ...tax, value: tax.id, label: tax.name })),
    [options, selectedIDs],
  );

  // The chip and its button are gone after the render, so focus would drop to
  // the page; it goes to the select instead, which is where the next step is.
  const removeAt = (index) => {
    onChange(selected.filter((_, position) => position !== index));
    requestAnimationFrame(() => selectRef.current?.focus());
  };

  if (error) {
    return (
      <ErrorSelect
        name="tax"
        customClassNames="mt-1"
        error={error}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <div className="mt-1 space-y-1">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1 list-none p-0 m-0">
          {selected.map((tax, index) => {
            const taxID = tax?.id ?? tax?.value ?? null;
            const live = taxID ? byID.get(taxID) : null;
            const name = live?.name ?? tax?.name ?? tax?.label ?? null;
            const withholding = Boolean(live?.withholding ?? tax?.withholding);
            const label = name || t("taxes.unavailable");

            return (
              <li
                key={taxID || index}
                className={clsx(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                  !name && "bg-gray-100 text-gray-500 italic",
                  name && withholding && "bg-amber-50 text-amber-800",
                  name && !withholding && "bg-blue-50 text-blue-700",
                )}
              >
                <span>{label}</span>
                {withholding && (
                  <span className="rounded bg-amber-100 px-1 not-italic text-amber-900">
                    {t("taxes.badges.withheld")}
                  </span>
                )}
                {!isDisabled && (
                  <button
                    type="button"
                    onClick={() => removeAt(index)}
                    aria-label={t("invoices.table.actions.removeTax", {
                      name: label,
                    })}
                    // 24px target; the negative margin keeps the chip 20px tall.
                    className="-my-1 -mr-1.5 rounded-full p-1.5 hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {selected.length >= max ? (
        // Disabling the select would leave a control that looks broken; saying
        // why there is nothing to pick explains itself.
        <p className="text-xs text-gray-500">
          {t("invoices.table.hints.maxTaxes", { max })}
        </p>
      ) : (
        <Select
          inputId={id}
          className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm w-full"
          classNamePrefix="select2-selection"
          options={available}
          // The select is an "add" control, not the field's value: holding no
          // value is what makes it reset itself after every pick.
          value={null}
          onChange={(option) => option && onChange([...selected, option])}
          placeholder={
            placeholder ||
            t(
              selected.length
                ? "invoices.table.placeholders.addTax"
                : "invoices.table.placeholders.tax",
            )
          }
          ref={selectRef}
          maxMenuHeight={150}
          noOptionsMessage={() => t("options.notAvailable")}
          loadingMessage={() => `${t("common.loading")}...`}
          styles={SELECT_STYLES}
          menuPortalTarget={document.body}
          isDisabled={isDisabled || isLoading}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ItemTaxPicker;
